/**
 * "Nuova chat": un campo unico dove si cerca un Lead o Cliente per nome oppure si
 * scrive un numero. Se l'utente vede piu' numeri aziendali sceglie da quale scrivere.
 * Sulle sessioni WAHA si verifica prima che il numero abbia WhatsApp; sui numeri
 * ufficiali (Cloud) non si puo': si avvisa che fuori dalle 24 ore serve un modello.
 * Poi la chat si apre nel drawer con openWithChat (la bozza la crea il drawer).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Phone, Loader2, Info, AlertCircle, User, Users } from 'lucide-react';
import { useApp } from '@/components/AppProvider';
import { useUserData } from '@/lib/auth';
import { useWhatsAppConfigs, type WhatsAppSessionConfig } from '@/lib/hooks/useWhatsAppConfigs';
import { useWhatsAppDrawerStore } from '@/lib/stores/whatsappDrawerStore';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { WahaApiService } from '@/lib/services/wahaApiService';
import { etichettaNumero } from '@/lib/services/whatsapp/phoneDigits';
import { formatPhoneForDisplay } from '@/lib/services/whatsapp/chatListRules';
import { classifyNewChatQuery, isCloudService, outcomeOfCheck } from '@/lib/services/whatsapp/newChatLogic';
import { searchClientsForChat, type ClientChatCandidate } from '@/lib/services/whatsapp/clientPhoneSearch';
import { tip } from '@/components/messages/ChatTooltip';
import { NewGroupModal } from '../group/NewGroupModal';

export interface NewChatTarget {
  digits: string;
  name: string | null;
}

interface Props {
  onClose: () => void;
  initialQuery?: string;
  /** Destinatario gia' scelto (es. dalla ricerca della lista): si salta la ricerca. */
  initialTarget?: NewChatTarget | null;
}

function defaultConfigId(configs: WhatsAppSessionConfig[]): string | null {
  const attivo = useWhatsAppChatStore.getState().wahaConfig?.configId;
  let salvato: string | null = null;
  try { salvato = localStorage.getItem('whatsapp_active_config'); } catch { /* storage non disponibile */ }
  return [attivo, salvato].find((id) => id && configs.some((c) => c.id === id)) || configs[0]?.id || null;
}

const configLabel = (c: WhatsAppSessionConfig) => {
  const numero = etichettaNumero(c.phoneNumber);
  return c.displayName ? `${c.displayName}${numero ? ` (${numero})` : ''}` : numero || 'WhatsApp';
};

export function NewChatModal({ onClose, initialQuery = '', initialTarget = null }: Props) {
  const { confirm } = useApp();
  const { organizationId, userProfile } = useUserData();
  const { configs, loading: configsLoading } = useWhatsAppConfigs();
  const [query, setQuery] = useState(initialQuery);
  const [configId, setConfigId] = useState<string | null>(null);
  const [clients, setClients] = useState<ClientChatCandidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const autoStarted = useRef(false);
  const [groupMode, setGroupMode] = useState(false);

  const parsed = useMemo(() => classifyNewChatQuery(query), [query]);
  const config = configs.find((c) => c.id === configId) || null;
  const cloud = isCloudService(config?.serviceName);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    if (!configId && configs.length > 0) setConfigId(defaultConfigId(configs));
  }, [configs, configId]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); if (!busy) onClose(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  // Ricerca Lead/Clienti per nome
  useEffect(() => {
    setProblem(null);
    if (parsed.kind !== 'name' || parsed.term.length < 2 || !organizationId) {
      setClients([]);
      setSearching(false);
      return;
    }
    let annullata = false;
    setSearching(true);
    setSearchError(false);
    const t = setTimeout(() => {
      searchClientsForChat(organizationId, userProfile?.id ?? null, parsed.term)
        .then((rows) => { if (!annullata) setClients(rows); })
        .catch(() => { if (!annullata) { setClients([]); setSearchError(true); } })
        .finally(() => { if (!annullata) setSearching(false); });
    }, 300);
    return () => { annullata = true; clearTimeout(t); };
  }, [parsed, organizationId, userProfile?.id]);

  const start = async (target: NewChatTarget) => {
    if (!config || busy) return;
    setProblem(null);
    let digits = target.digits;
    if (!isCloudService(config.serviceName) && config.session) {
      setBusy(true);
      const res = await new WahaApiService({
        apiUrl: '', apiKey: '', session: config.session, phoneNumber: config.phoneNumber,
        organizationId: organizationId || '', configId: config.id, serviceName: config.serviceName,
      }).checkNumberExists(digits);
      setBusy(false);
      const esito = outcomeOfCheck(res);
      if (esito === 'missing') {
        setProblem(`Il numero ${formatPhoneForDisplay(digits)} non risulta su WhatsApp. Controlla che sia scritto giusto.`);
        return;
      }
      if (esito === 'unknown') {
        const ok = await confirm({
          title: 'Verifica non riuscita',
          message: `Non sono riuscito a verificare se ${formatPhoneForDisplay(digits)} ha WhatsApp (il numero ${configLabel(config)} potrebbe non essere collegato). Vuoi aprire comunque la chat?`,
          confirmText: 'Apri comunque',
          cancelText: 'Annulla',
        });
        if (!ok) return;
      } else {
        // WAHA puo' correggere il numero (es. nona cifra dei cellulari brasiliani): si usa il suo.
        const jid = res.pn || (res.chatId?.endsWith('@c.us') ? res.chatId : '');
        const corretto = jid ? jid.split('@')[0].replace(/\D/g, '') : '';
        if (corretto.length >= 8) digits = corretto;
      }
    }
    useWhatsAppDrawerStore.getState().openWithChat(digits, config.id, target.name);
    onClose();
  };

  // Destinatario gia' scelto: con un solo numero aziendale si parte subito.
  useEffect(() => {
    if (!initialTarget || autoStarted.current || !config || configs.length !== 1) return;
    autoStarted.current = true;
    void start(initialTarget);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTarget, config, configs.length]);

  // Nuovo gruppo: solo numeri Standard (WAHA). Sui numeri Ufficiale i gruppi non esistono.
  if (groupMode && config && !cloud && config.session) {
    return (
      <NewGroupModal session={config.session} configId={config.id}
        numberLabel={configs.length > 1 ? configLabel(config) : null}
        onBack={() => setGroupMode(false)} onClose={onClose} />
    );
  }

  const rowCls = 'w-full flex items-center gap-3 px-3 py-2 rounded-md text-left hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed';

  return createPortal(
    <div className="fixed inset-0 z-[9000] flex items-start justify-center bg-black/30 px-4 pt-[12vh]" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label="Nuova chat" className="w-full max-w-md rounded-xl bg-white dark:bg-[#252528] shadow-2xl border border-[rgba(55,53,47,0.09)] dark:border-white/10 overflow-hidden">
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <h3 className="text-sm font-semibold text-[#37352F] dark:text-white">Nuova chat WhatsApp</h3>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Chiudi" {...tip('Chiudi')} className="p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10">
            <X className="w-4 h-4 text-[#787774]" />
          </button>
        </div>

        {configs.length > 1 && (
          <label className="flex items-center gap-2 px-4 pb-2 text-xs text-[#787774]">
            <span className="flex-shrink-0">Scrivi da</span>
            <select value={configId || ''} onChange={(e) => setConfigId(e.target.value)} disabled={busy}
              className="flex-1 min-w-0 text-xs rounded-md border border-[rgba(55,53,47,0.16)] dark:border-white/10 bg-transparent px-2 py-1 text-[#37352F] dark:text-white">
              {configs.map((c) => <option key={c.id} value={c.id}>{configLabel(c)}</option>)}
            </select>
          </label>
        )}

        <div className="px-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#787774]" />
            <input ref={inputRef} type="text" value={query} onChange={(e) => setQuery(e.target.value)} disabled={busy}
              placeholder="Nome del Lead o Cliente, oppure un numero"
              className="w-full h-9 pl-9 pr-3 rounded-md border border-[rgba(55,53,47,0.16)] dark:border-white/10 bg-white dark:bg-white/5 text-sm text-[#37352F] dark:text-white placeholder:text-[#787774] focus:outline-none focus:border-[#2383E2] focus:ring-2 focus:ring-[#2383E2]/20"
              onKeyDown={(e) => { if (e.key === 'Enter' && parsed.kind === 'phone') void start({ digits: parsed.digits, name: null }); }}
            />
          </div>
        </div>

        {cloud && (
          <p className="mx-4 mb-2 flex gap-2 rounded-md bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
            <Info className="w-4 h-4 flex-shrink-0" />
            Numero ufficiale: se questa persona non ti ha scritto nelle ultime 24 ore, il primo messaggio deve essere un modello approvato. Nella chat trovi il pulsante per sceglierlo.
          </p>
        )}
        {problem && (
          <p className="mx-4 mb-2 flex gap-2 rounded-md bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-300">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />{problem}
          </p>
        )}

        <div className="max-h-[45vh] overflow-y-auto px-2 pb-3">
          {!busy && config && !cloud && config.session && parsed.kind === 'empty' && !initialTarget && (
            <button type="button" className={rowCls} onClick={() => setGroupMode(true)}
              {...tip('Crea un gruppo WhatsApp con Lead, Clienti o numeri')}>
              <span className="w-8 h-8 rounded-full bg-[rgba(35,131,226,0.08)] flex items-center justify-center"><Users className="w-4 h-4 text-[#2383E2]" /></span>
              <span className="text-sm text-[#37352F] dark:text-white">Nuovo gruppo</span>
            </button>
          )}
          {!configsLoading && configs.length === 0 && (
            <p className="px-3 py-4 text-sm text-[#787774]">Non hai numeri WhatsApp da cui scrivere. Chiedi a un amministratore di darti accesso a un numero.</p>
          )}
          {busy && (
            <p className="flex items-center gap-2 px-3 py-3 text-sm text-[#787774]"><Loader2 className="w-4 h-4 animate-spin" />Verifico che il numero abbia WhatsApp...</p>
          )}
          {!busy && parsed.kind === 'phone' && (
            <button type="button" className={rowCls} disabled={!config} onClick={() => void start({ digits: parsed.digits, name: null })}>
              <span className="w-8 h-8 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center"><Phone className="w-4 h-4 text-emerald-600" /></span>
              <span className="text-sm text-[#37352F] dark:text-white">Scrivi a <strong>{formatPhoneForDisplay(parsed.digits)}</strong></span>
            </button>
          )}
          {!busy && parsed.kind === 'name' && (
            <>
              {searching && <p className="flex items-center gap-2 px-3 py-3 text-sm text-[#787774]"><Loader2 className="w-4 h-4 animate-spin" />Cerco fra Lead e Clienti...</p>}
              {!searching && searchError && <p className="px-3 py-3 text-sm text-red-600">Ricerca non riuscita. Riprova tra poco.</p>}
              {!searching && !searchError && parsed.term.length >= 2 && clients.length === 0 && (
                <p className="px-3 py-3 text-sm text-[#787774]">Nessun Lead o Cliente trovato. Puoi anche scrivere direttamente il numero.</p>
              )}
              {!searching && clients.map((c) => (
                <div key={c.id} className="py-0.5">
                  {c.numbers.length === 0 ? (
                    <div className={`${rowCls} opacity-60 cursor-default hover:bg-transparent`}>
                      <span className="w-8 h-8 rounded-full bg-[rgba(55,53,47,0.06)] flex items-center justify-center"><User className="w-4 h-4 text-[#787774]" /></span>
                      <span className="min-w-0 flex-1"><span className="block truncate text-sm text-[#37352F] dark:text-white">{c.name}</span><span className="text-xs text-[#787774]">{c.kind} senza numero salvato</span></span>
                    </div>
                  ) : c.numbers.map((num) => (
                    <button key={num} type="button" className={rowCls} disabled={!config} onClick={() => void start({ digits: num, name: c.name })}>
                      <span className="w-8 h-8 rounded-full bg-[rgba(35,131,226,0.08)] flex items-center justify-center"><User className="w-4 h-4 text-[#2383E2]" /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-[#37352F] dark:text-white">{c.name}</span>
                        <span className="block truncate text-xs text-[#787774]">{c.kind}{c.contactName && c.contactName !== c.name ? ` · ${c.contactName}` : ''} · {formatPhoneForDisplay(num)}</span>
                      </span>
                    </button>
                  ))}
                </div>
              ))}
            </>
          )}
          {!busy && initialTarget && parsed.kind === 'empty' && (
            <button type="button" className={rowCls} disabled={!config} onClick={() => void start(initialTarget)}>
              <span className="w-8 h-8 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center"><Phone className="w-4 h-4 text-emerald-600" /></span>
              <span className="text-sm text-[#37352F] dark:text-white">Scrivi a <strong>{initialTarget.name || formatPhoneForDisplay(initialTarget.digits)}</strong></span>
            </button>
          )}
          {!busy && parsed.kind === 'empty' && !initialTarget && configs.length > 0 && (
            <p className="px-3 py-3 text-xs text-[#787774]">Scrivi il nome di un Lead o Cliente, oppure un numero con o senza +39.</p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
